import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Calendar, DollarSign, Building2, PiggyBank, HeartHandshake, TrendingUp, Calculator } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.09 } } };

/* @section: tax-planning */
export default function TaxPlanning() {
  const { t } = useLanguage();
  const planningAreas = [{
      icon: <Building2 className="w-5 h-5" />,
      en: "Entity Structure Review: Sole Prop, LLC, S-Corp",
      ko: "사업 구조 검토: 개인사업자, LLC, S-Corp",
      detailEn: "We analyze whether your current entity structure is optimal for your tax situation and business goals.",
      detailKo: "현재 사업 구조가 세금 상황과 사업 목표에 최적인지 분석합니다.",
    },
    {
      icon: <DollarSign className="w-5 h-5" />,
      en: "Reasonable Salary & S-Corp Planning",
      ko: "합리적 급여 및 S-Corp 계획",
      detailEn: "Determine the right W-2 salary to minimize self-employment tax while staying compliant with IRS guidelines.",
      detailKo: "IRS 지침을 준수하면서 자영업세를 최소화하는 적절한 W-2 급여를 결정합니다.",
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      en: "Deduction Strategy",
      ko: "공제 전략",
      detailEn: "Identify and maximize deductible business expenses, home office, vehicle, health insurance, and more.",
      detailKo: "사업 비용, 홈오피스, 차량, 건강보험 등 공제 가능한 항목을 찾아 최대화합니다.",
    },
    {
      icon: <Calendar className="w-5 h-5" />,
      en: "Estimated Tax Planning",
      ko: "예상세금 계획",
      detailEn: "Calculate and schedule quarterly estimated tax payments to avoid penalties and surprise tax bills.",
      detailKo: "가산세와 세금 폭탄을 피하기 위한 분기별 예상세금을 계산하고 일정을 잡습니다.",
    },
    {
      icon: <PiggyBank className="w-5 h-5" />,
      en: "Retirement Plan Options",
      ko: "은퇴 플랜 옵션",
      detailEn: "Explore SEP-IRA, Solo 401(k), and other retirement vehicles to reduce taxable income significantly.",
      detailKo: "SEP-IRA, Solo 401(k) 등 과세 소득을 크게 줄일 수 있는 은퇴 플랜을 탐색합니다.",
    },
    {
      icon: <HeartHandshake className="w-5 h-5" />,
      en: "Health Insurance & Accountable Plan Review",
      ko: "건강보험 및 Accountable Plan 검토",
      detailEn: "Review self-employed health insurance deduction and set up accountable plans for employee expense reimbursements.",
      detailKo: "자영업자 건강보험 공제와 직원 비용 상환을 위한 Accountable Plan 셋업을 검토합니다.",
    },
    {
      icon: <Calculator className="w-5 h-5" />,
      en: "Year-End Tax Projection",
      ko: "연말 세금 예측",
      detailEn: "Run year-end projections to make informed decisions before December 31st to minimize your tax bill.",
      detailKo: "12월 31일 전에 정보에 입각한 결정을 내려 세금을 최소화하기 위한 연말 예측을 실행합니다.",
    },
  ];

  const myths = [
    {
      mythEn: "\"Tax planning is only for big companies.\"",
      mythKo: "\"절세는 대기업만 하는 것이다.\"",
      realityEn: "Small businesses benefit even more from proactive planning because the tax savings directly impact personal income.",
      realityKo: "절세는 개인 소득에 직접 영향을 미치므로 스몰비즈니스가 더 큰 효과를 볼 수 있습니다.",
    },
    {
      mythEn: "\"I just need to deduct more expenses.\"",
      mythKo: "\"비용을 더 많이 넣으면 된다.\"",
      realityEn: "Real tax planning requires reviewing entity structure, compensation, retirement plans, and timing — not just expenses.",
      realityKo: "진정한 절세는 사업 구조, 급여, 은퇴 플랜, 타이밍을 함께 검토해야 합니다 — 단순히 비용만이 아닙니다.",
    },
    {
      mythEn: "\"My CPA handles it during tax season.\"",
      mythKo: "\"세금보고 시즌에 CPA가 다 해준다.\"",
      realityEn: "Tax planning decisions made after December 31st have limited impact. The best strategies require action throughout the year.",
      realityKo: "12월 31일 이후의 절세 결정은 효과가 제한됩니다. 최고의 전략은 연중 내내 실행되어야 합니다.",
    },
  ];

  return (
    <>
      <SEO
        title="Small Business Tax Planning San Jose | NexBiz Tax Advisor"
        description="Small Business Tax Planning for LLCs, S-Corps, and self-employed professionals in San Jose and Bay Area. Enrolled Agent tax strategy, estimated tax, and year-round planning."
      />
      {/* Page Header */}
      <section className="bg-primary py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-accent text-sm font-semibold uppercase tracking-wider mb-3">
              {t("Small Business Tax Planning", "스몰비즈니스 절세 플랜")}
            </motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              {t(
                "Small Business Tax Planning",
                "스몰비즈니스 절세 플랜"
              )}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-primary-foreground/70 text-lg max-w-2xl mx-auto">
              {t(
                "Tax Planning Designed for LLCs, S-Corps and Self-Employed Professionals in San Jose and the Bay Area.",
                "샌호세 및 베이 지역 LLC, S-Corp, 자영업자를 위한 절세 플랜."
              )}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 md:py-20 px-4 bg-muted/40">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <div className="bg-white border-l-4 border-accent rounded-xl p-6 shadow-sm">
              <p className="text-foreground leading-relaxed text-base">
                {t(
                  "Smart tax planning for small businesses isn't about stuffing in deductions — it's about reviewing your entity structure, salary strategy, retirement plans, estimated taxes, and bookkeeping together. At NexBiz, we work with you throughout the year, not just at filing time.",
                  "스몰비즈니스의 절세는 단순히 비용을 많이 넣는 것이 아니라, 사업 구조, 급여, 은퇴플랜, 예상세금, 장부 상태를 함께 검토해야 합니다. NexBiz는 신고 시기뿐만 아니라 연중 내내 함께합니다."
                )}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Planning Areas */}
      <section className="py-16 md:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-foreground text-center mb-3">
              {t("What We Cover in Tax Planning", "절세 계획에서 다루는 항목")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              {t(
                "A comprehensive review of every tax lever available to your business.",
                "비즈니스에 적용 가능한 모든 절세 방법을 종합적으로 검토합니다."
              )}
            </motion.p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {planningAreas.map((area, i) => (
                <motion.div key={i} variants={fadeUp} className="bg-card border border-border rounded-xl p-5 hover:border-accent/50 hover:shadow-sm transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center text-accent shrink-0">
                      {area.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1.5">{t(area.en, area.ko)}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{t(area.detailEn, area.detailKo)}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Myths & Reality */}
      <section className="py-16 md:py-20 px-4 bg-muted">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground text-center mb-10">
              {t("Common Tax Planning Myths", "절세에 대한 흔한 오해")}
            </motion.h2>
            <div className="space-y-5">
              {myths.map((m, i) => (
                <motion.div key={i} variants={fadeUp} className="bg-white rounded-xl border border-border overflow-hidden">
                  <div className="px-6 py-4 bg-destructive/5 border-b border-border">
                    <p className="font-semibold text-foreground italic">{t(m.mythEn, m.mythKo)}</p>
                  </div>
                  <div className="px-6 py-4 flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 shrink-0" />
                    <p className="text-sm text-foreground leading-relaxed">{t(m.realityEn, m.realityKo)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 px-4">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl font-bold text-foreground mb-4">
              {t("Start Your Tax Planning Today", "오늘부터 절세 계획을 시작하세요")}
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground mb-8">
              {t(
                "Don't wait until April. The best tax-saving decisions are made throughout the year.",
                "4월까지 기다리지 마세요. 최고의 절세 결정은 연중 내내 이루어집니다."
              )}
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity shadow-md">
                {t("Schedule a Tax Planning Session", "절세 계획 상담 예약하기")}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/services" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-lg border border-primary text-primary font-semibold hover:bg-primary hover:text-primary-foreground transition-all">
                {t("View All Services", "전체 서비스 보기")}
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
